import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chime } from "@/lib/alert";

export const NotificationBell = ({ dark = false, onNew, loud = false }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const lastSeen = useRef(null);

  const go = useCallback((n) => {
    if (n.link) return nav(n.link);
    if (n.inspection_id) nav(user.role === "admin" ? `/admin/inspections/${n.inspection_id}` : `/agent/inspections/${n.inspection_id}`);
  }, [nav, user.role]);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/notifications");
    const newest = d.items[0];
    const first = lastSeen.current === null;
    if (!first && newest && newest.id !== lastSeen.current && !newest.read) {
      if (loud) {
        chime();
        toast.success(newest.title, { description: newest.body, duration: 12000, className: "loud-toast", action: { label: "فتح", onClick: () => go(newest) } });
      } else toast.info(newest.title, { description: newest.body });
      onNew?.();
    } else if (first && loud && d.unread > 0 && newest && !newest.read && sessionStorage.getItem("fi_alerted") !== newest.id) {
      chime();
      toast.success(d.unread > 1 ? `لديك ${d.unread} إشعارات جديدة` : newest.title, { description: newest.body, duration: 12000, className: "loud-toast", action: { label: "فتح", onClick: () => go(newest) } });
    }
    lastSeen.current = newest?.id || "";
    if (loud && newest) sessionStorage.setItem("fi_alerted", newest.id);
    setData(d);
  }, [onNew, loud, go]);

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 12000);
    return () => clearInterval(t);
  }, [load]);

  const open = async (o) => {
    if (o && data.unread) {
      await api.post("/notifications/read-all");
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <Popover onOpenChange={open}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="الإشعارات"
          className={`relative h-11 w-11 grid place-items-center rounded-lg border transition-colors ${dark ? "border-line hover:bg-panel-hover" : "border-slate-200 bg-white hover:bg-slate-100"}`}>
          <Bell size={19} />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -top-1 -left-1 min-w-5 h-5 px-1 rounded-full bg-brand-red text-white text-[11px] font-bold grid place-items-center">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" dir="rtl">
        <p className="px-4 py-3 border-b font-bold text-sm">الإشعارات</p>
        <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
          {data.items.length === 0 && <p className="p-4 text-sm text-muted-foreground">لا توجد إشعارات</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => go(n)} data-testid={`notification-item-${n.id}`}
              className="w-full text-start px-4 py-3 border-b last:border-0 hover:bg-muted transition-colors">
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line" dir="auto">{n.body}</p>
              <p className="text-[11px] text-muted-foreground mt-1 font-mono">{fmtDate(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
